import Vue from "vue";
import VueRouter from "vue-router";

import routes from "@/router/routes";

import { auth, authz, resolve, saveScrollPosition } from "@/router/guards";

// /!\ This is not the cleanest of solutions /!\ //
//
// `NavigationDuplicated` errors don't bubble up to the root router, so we cannot use the error handler
// registered below (`router.onError`) to ignore it.
// Hence we overwrite the `VueRouter` prototype to specifically ignore those errors on `router.push()` and `router.replace()`.
//
// More info: https://router.vuejs.org/guide/advanced/navigation-failures.html
//
const { isNavigationFailure } = VueRouter;
const routerPush = VueRouter.prototype.push;
const routerReplace = VueRouter.prototype.replace;

const catchFailure = promise =>
    promise.catch(error => {
        // Ignore navigation failures
        if (isNavigationFailure(error)) {
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.error(error);
            }

            return;
        }

        // Throw any other types of errors
        throw error;
    });

VueRouter.prototype.push = async function push(...args) {
    const pushPromise = routerPush.call(this, ...args);

    if (!pushPromise) return;

    return catchFailure(pushPromise);
};

VueRouter.prototype.replace = async function push(...args) {
    const replacePromise = routerReplace.call(this, ...args);

    if (!replacePromise) return;

    return catchFailure(replacePromise);
};

Vue.use(VueRouter);

const router = new VueRouter({
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        if (from.meta?.position) {
            return from.meta.position;
        }
        return from;
    },
});

// Note: resolver order is important
router.beforeEach(auth);
router.beforeEach(authz);
router.beforeEach(resolve);
router.beforeEach(saveScrollPosition);

let reloadTimeout = null;
router.onError(error => {
    // Mitigate a blank page when loading outdated views (with unique filename hashes per build)
    // after & during a rolling deployment while we have no CDN or blue/green deployment.
    const isChunkLoadFailure =
        error.name === "ChunkLoadError" || error.message?.match(/Loading chunk ([\w-])* failed/gi);

    if (isChunkLoadFailure) {
        const queryValue = router.history?.pending?.query?.retry;
        const retryCount = queryValue ? parseInt(queryValue, 10) + 1 : 1;

        // we only do an automatic try once to not confuse the user
        if (retryCount <= 1) {
            // stick to same timing for single error occurence
            clearTimeout(reloadTimeout);

            // in hopes to get the latest index.html and resource files from an updated node
            reloadTimeout = setTimeout(() => {
                // needs to be set in here as it could trigger a route change handled by vuew
                // outside of timings otherwise
                window.location.hash = `${router.history?.pending?.path}?retry=${retryCount}`;
                window.location.reload();
            }, 1000);
        } else {
            // display error with manual reload option as fallback
            router.replace({
                path: "/error/chunk-load-failure",
                query: { redirect: router.history?.pending?.path },
            });
        }
    }
});

// Appcues
//
router.afterEach(() => {
    // Manually call `window.Appcues.page()` every time we are navigating to a new screen
    // so that it is aware of the route change.
    //
    // More info: https://docs.appcues.com/article/389-single-page-applications-spas
    //
    if (window.Appcues) {
        window.Appcues.page();
    }
});

// Scroll to top
//
router.afterEach(() => {
    window.scrollTo({ top: 0 });
});

export default router;
